import {FullTextSearchResult} from "@stores/SearchStore";
import {WorkspaceModelType} from "@stores/WorkspaceStore";
import {RootStoreModel} from "@stores/RootStore";
import {UserSessionState} from "@stores/UserSessionState";
import {logErrorToSentry} from "../utils/errors/logErrorToSentry";
import {registerSearchRequest} from "./metric/metricActions";
import {CrudServices} from "../platform/services/types/ServiceDependencies";

import {queryGqlApi} from "../graphql/queryGqlApi";

import {searchDocuments} from '../graphql/queries';
import {SEARCH_DOCUMENTS_POSTGRES, SearchResponse, SearchFilters} from '../graphql/searchQueries';



export const searchContent = async (
    services: CrudServices,
    rootStore: RootStoreModel,
    userState: UserSessionState
) => {
    if (rootStore.search.isSearching || rootStore.search.queryPerformed) {
        return;
    }
    try {
        await _searchContent(services, rootStore.search.textInput, rootStore, userState);
        registerSearchRequest(userState.wsServerConnection);
    } catch (error: unknown) {
        logErrorToSentry(error as Error);
    }
}

export const _searchContent = async (
    services: CrudServices,
    textToSearch: string,
    rootStore: RootStoreModel,
    userState: UserSessionState,
) => {
    const workspace = rootStore.view.workspace!;
    if (rootStore.search.isSearching) {
        return;
    }
    rootStore.search.setIsSearching(true);
    
    try {
        // Use new PostgreSQL search via prosemirror-server GraphQL API
        const searchResponse = await _searchContentPostgreSQL(
            textToSearch,
            workspace,
            services
        );
        // Convert PostgreSQL search results to frontend format
        const formattedResults: FullTextSearchResult[] = searchResponse.items.map(result => ({
            id: result.itemId,
            itemId: result.itemId,
            highlight: result.snippet ? {
                content: result.snippet
            } : undefined
        }));
        
        rootStore.search.setFullTextSearchResults(formattedResults);
    } catch (error) {
        console.error('PostgreSQL search failed, falling back to legacy search:', error);
        
        // Fallback to legacy OpenSearch if PostgreSQL search fails
        await _searchContentLegacy(services, textToSearch, workspace, rootStore);
    }
}

/**
 * New PostgreSQL search implementation via prosemirror-server GraphQL API
 */
const _searchContentPostgreSQL = async (
    textToSearch: string,
    workspace: WorkspaceModelType,
    services: CrudServices
): Promise<SearchResponse> => {
    // Prepare search filters based on workspace view
    const filters: SearchFilters = {};
    
    if (workspace.view === 'trash') {
        filters.trashed = true;
    } else if (workspace.view === 'archive') {
        filters.archived = true;
    } else {
        filters.trashed = false;
        filters.archived = false;
    }
    
    // Make GraphQL request to prosemirror-server using the pgServer endpoint
    const response = await queryGqlApi(
        SEARCH_DOCUMENTS_POSTGRES,
        {
            query: textToSearch,
            workspaceId: workspace.id,
            filters: filters,
            options: {
                limit: 20
            }
        },
        services.dataClientService,
        services.authService,
        'pgServer' // Use the pgServer endpoint for prosemirror-server GraphQL
    );
    
    return response;
}

/**
 * Legacy OpenSearch implementation (fallback)
 */
const _searchContentLegacy = async (
    services: CrudServices,
    textToSearch: string,
    workspace: WorkspaceModelType,
    rootStore: RootStoreModel
) => {
    const filterConditions: any = {
        content: {
            matchPhrasePrefix: textToSearch,
        },
    };
    if (workspace.view === 'trash') {
        filterConditions.trashed = {
            eq: true,
        }
    } else if (workspace.view === 'archive') {
        filterConditions.archived = {
            eq: true,
        };
    } else {
        filterConditions.trashed = {
            eq: false,
        };
        filterConditions.archived = {
            eq: false,
        };
    }
    const results = await queryGqlApi(
        searchDocuments,
        {
            filter: filterConditions
        },
        services.dataClientService,
        services.authService,
        'main',
    );

    if (!results.items) {
        rootStore.search.setFullTextSearchResults([]);
    } else {
        rootStore.search.setFullTextSearchResults(results.items as unknown as FullTextSearchResult[]);
    }
}
